// OCR transcriptions of media, served by the tools service (GET /ocr/media/{handle}).
// The tools service only answers when Gramps accepts the caller's token for that
// media, so the request carries the same per-tree token as the Gramps API calls.

import { isMarchandViveretHost } from '../grampsAuth';

export type OcrZoneType = 'target_record' | 'other_record' | 'marginal_note' | 'header' | 'signatures' | 'other';

export interface OcrZone {
    type: OcrZoneType;
    /** [x0, y0, x1, y1] in thousandths of the image width/height, origin top-left. */
    bbox: number[];
    text: string;
    uncertain: boolean;
}

export interface OcrActPage {
    image: number;
    media_handle: string;
    gramps_id: string | null;
    kind: string | null;
}

/** Translation of the act into its tree's language (fr, or es for Paucara). */
export interface OcrTranslation {
    language: string;
    model: string | null;
    updated_at: string;
    /** The target record(s), translated, in reading order. */
    target_text: string | null;
    /** This page's translated zones, by the zone's index in `page.zones`. */
    zones: Record<string, string>;
}

export interface OcrAct {
    act_id: string;
    event_handle: string | null;
    model: string | null;
    updated_at: string;
    summary_fr: string | null;
    target_text: string | null;
    languages: string[];
    scripts: string[];
    legibility: string | null;
    resolution_limited: boolean;
    persons: { name: string; role: string }[];
    /** Every page of the act, in reading order. */
    pages: OcrActPage[];
    /** This media's page: its zones and the size of the original file. */
    page: {
        image: number;
        kind: string | null;
        sent: [number, number];
        original: [number, number];
        zones: OcrZone[];
    };
    /** Null when the act is already in the tree's language or not translated yet. */
    translation?: OcrTranslation | null;
}

export interface MediaOcr {
    media_handle: string;
    acts: OcrAct[];
}

function isLocalhostHost(hostname: string): boolean {
    return hostname === 'localhost' || hostname === '127.0.0.1';
}

export function getToolsApiBase(): string {
    if (isMarchandViveretHost()) {
        return '';
    }
    // VITE_TOOLS_BASE_URL is the name; VITE_MEDIA_GATEWAY_BASE_URL is what it
    // used to be called, when this service only served media. Kept as a
    // fallback so a deployment can be renamed without a flag day.
    const envBase = ((import.meta.env.VITE_TOOLS_BASE_URL
        ?? import.meta.env.VITE_MEDIA_GATEWAY_BASE_URL) as string | undefined)?.trim();
    if (envBase) {
        return envBase.replace(/\/+$/, '');
    }
    return isLocalhostHost(window.location.hostname) ? 'http://localhost:8001' : 'https://tools.genealhistory.eu';
}

export async function fetchMediaOcr(mediaHandle: string, token?: string): Promise<MediaOcr> {
    const headers: Record<string, string> = {};
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(`${getToolsApiBase()}/ocr/media/${encodeURIComponent(mediaHandle)}`, {
        headers,
    });
    // No transcription (or no access) is not an error for the viewer: it just has nothing to show.
    if (response.status === 404 || response.status === 403) {
        return { media_handle: mediaHandle, acts: [] };
    }
    if (!response.ok) {
        throw new Error(`OCR request failed: HTTP ${response.status}`);
    }
    return (await response.json()) as MediaOcr;
}

/** Which of these media have a transcription (only among those the token may see). */
export async function fetchMediaWithOcr(mediaHandles: string[], token?: string): Promise<string[]> {
    const query = new URLSearchParams({ handles: mediaHandles.join(',') });
    const headers: Record<string, string> = {};
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(`${getToolsApiBase()}/ocr/media?${query}`, {
        headers,
    });
    if (!response.ok) {
        // Badges are a hint: no answer means no badge, not an error to surface.
        return [];
    }
    return ((await response.json()) as { handles: string[] }).handles;
}

/** Someone an act names as witness, informant or godparent, as the links
 * machine read them (tools/ocr/link_people.py). */
export interface ActPerson {
    written_name: string;
    role: 'witness' | 'informant' | 'godparent';
    given_names?: string | null;
    surname?: string | null;
    /** 'M', 'F' or '' when the act does not say. */
    sex?: string | null;
    /** The age the act states, 0 when it states none. */
    age?: number | null;
    occupation?: string | null;
    residence?: string | null;
    /** The passage that names them. */
    quote?: string | null;
    /** resolved: already linked to the event; unique / tied: proposed in the
     * atelier; none / too_vague: nobody in the tree. */
    outcome: 'resolved' | 'unique' | 'tied' | 'too_vague' | 'none';
    /** The people of the tree it may be, by handle. */
    candidates: string[];
}

/** The people the acts of an event name, in the tree or not. */
export async function fetchEventPeople(eventHandle: string, token?: string): Promise<ActPerson[]> {
    const headers: Record<string, string> = {};
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(`${getToolsApiBase()}/ocr/events/${encodeURIComponent(eventHandle)}/people`, {
        headers,
    });
    // Nothing read for this event, or no access: nothing to add to it.
    if (response.status === 404 || response.status === 403) {
        return [];
    }
    if (!response.ok) {
        throw new Error(`Event people request failed: HTTP ${response.status}`);
    }
    return ((await response.json()) as { people: ActPerson[] }).people;
}
